"use client";

import React, { useCallback, useEffect, useState } from 'react';
import { GameType } from '@/lib/types';
import Link from 'next/link';
import Image from 'next/image';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

type PropType = {
  games: GameType[];
};

const CategoryCarousel: React.FC<PropType> = ({ games }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  if (!games || games.length === 0) {
    return null;
  }

  const getCardClass = (index: number) => {
    const rotations = ["rotate-0", "rotate-1", "-rotate-1"];
    return rotations[index % rotations.length] || "rotate-0";
  };

  return (
    <div className="relative group hidden sm:block">
      <style jsx>{`
        .game-card {
          background: linear-gradient(145deg, #ffffff, #f0f0f0);
          box-shadow: 20px 20px 60px #d9d9d9,
                      -20px -20px 60px #ffffff;
          border: none;
        }
        .game-card:hover {
          box-shadow: 25px 25px 75px #d9d9d9,
                      -25px -25px 75px #ffffff;
        }
        .game-thumbnail {
          filter: brightness(0.95);
          transition: all 0.3s ease;
        }
        .game-card:hover .game-thumbnail {
          filter: brightness(1);
        }
        .nav-button {
          background: linear-gradient(145deg, #ff8c00, #ff6b00);
          box-shadow: 5px 5px 10px #d9d9d9,
                      -5px -5px 10px #ffffff;
          color: white;
        }
        .nav-button:hover {
          background: linear-gradient(145deg, #ff6b00, #ff8c00);
          box-shadow: 8px 8px 15px #d9d9d9,
                      -8px -8px 15px #ffffff;
        }
        .nav-button:disabled {
          background: linear-gradient(145deg, #cccccc, #e6e6e6);
          box-shadow: none;
        }
        .stats-overlay {
          background: linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0.4));
        }
        .category-badge {
          background: linear-gradient(145deg, #ff8c00, #ff6b00);
          color: white;
          border: none;
        }
        .embla {
          overflow: hidden;
        }
        .embla__container {
          display: flex;
          gap: 1.5rem;
          padding: 1rem;
        }
        .embla__slide {
          flex: 0 0 auto;
          min-width: 0;
          width: 48%;
        }
        @media (min-width: 640px) {
          .embla__slide {
            width: 31%;
          }
        }
        @media (min-width: 768px) {
          .embla__slide {
            width: 23%;
          }
        }
        @media (min-width: 1024px) {
          .embla__slide {
            width: 18%;
          }
        }
        @media (min-width: 1280px) {
          .embla__slide {
            width: 15%;
          }
        }
      `}</style>

      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {games.map((game, index) => (
            <div className="embla__slide" key={game.id}>
              <Link
                href={`/games/${game.slug}`}
                className="block group/slide game-card-link h-full"
              >
                <Card className={`overflow-hidden h-full game-card rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 ${getCardClass(index)} flex flex-col`}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden flex-shrink-0">
                    <Image
                      src={game.thumbnail || "/placeholder.png"}
                      alt={game.title}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, (max-width: 1280px) 20vw, 16.6vw"
                      className="game-thumbnail object-cover transition-transform duration-500 ease-in-out group-hover/slide:scale-110"
                    />
                    {game.isNew && (
                      <Badge className="new-badge absolute top-2 right-2 px-3 py-1 text-xs bg-gradient-to-r from-orange-500 to-red-500 text-white border-none">NEW ✨</Badge>
                    )}
                    <div className="absolute inset-0 stats-overlay opacity-0 group-hover/slide:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <div className="flex items-center space-x-3 text-white text-sm font-medium">
                        <span className="flex items-center gap-1">👁️ {game.views?.toLocaleString() || 0}</span>
                        <span className="flex items-center gap-1">▶️ {game.plays?.toLocaleString() || 0}</span>
                        <span className="flex items-center gap-1">❤️ {game.likes?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 flex-grow flex flex-col justify-between">
                    <h3 className="game-title font-bold line-clamp-1 text-sm md:text-base text-orange-600 group-hover/slide:text-orange-500 transition-colors mb-2">
                      {game.title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {game.categories?.slice(0, 2).map((cat, idx) => (
                        <Badge key={idx} className="category-badge text-xs px-2 py-1 rounded-full">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Card>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <button
        className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-4 z-10 nav-button rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
        onClick={scrollPrev}
        disabled={!prevBtnEnabled}
        aria-label="Previous Game"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-4 z-10 nav-button rounded-full p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
        onClick={scrollNext}
        disabled={!nextBtnEnabled}
        aria-label="Next Game"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
    </div>
  );
};

// Helper to hide scrollbar (add this utility class to your global CSS if needed)
// .scrollbar-hide::-webkit-scrollbar { display: none; }
// .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

export default CategoryCarousel; 